import { alpha, Button, styled } from "@mui/material";
import { isValidElement, MouseEvent, ReactNode } from "react";
import { useGlobalConfig } from "context/GlobalContext";
import { ANALYTICS_EVENTS } from "framework/analytics/AnalyticsEvents";


export const BglCloseButtonBase = styled(Button)(({theme}) => ({
  backgroundColor: theme.palette.error.main,
  borderColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  ":hover" : {
    backgroundColor: theme.palette.error.dark,
    borderColor: theme.palette.error.dark
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.error.main, 0.25)
      : alpha(theme.palette.error.main, 0.18),
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.error.main, 0.45)
      : alpha(theme.palette.error.main, 0.3),
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.error.contrastText, 0.7)
      : theme.palette.action.disabled,
    boxShadow: 'none'
  },
  textTransform : 'none'
}))

export const BglPrimaryButtonBase = styled(Button)(({theme}) => ({
  backgroundColor: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  ":hover" : {
    backgroundColor: theme.palette.primary.dark,
    borderColor: theme.palette.primary.dark
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.25)
      : alpha(theme.palette.primary.main, 0.18),
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.45)
      : alpha(theme.palette.primary.main, 0.3),
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.contrastText, 0.7)
      : theme.palette.action.disabled,
    boxShadow: 'none'
  },
  textTransform : 'none'
}))

export const BglSecondaryButtonBase = styled(Button)(({theme}) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  border: `1px solid ${theme.palette.secondary.light}`,
  borderRadius: 8,
  boxShadow: 'none',
  ":hover" : {
    backgroundColor: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    color: theme.palette.primary.contrastText
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.secondary.main, 0.25)
      : alpha(theme.palette.secondary.main, 0.18),
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.secondary.light, 0.45)
      : alpha(theme.palette.secondary.light, 0.3),
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.secondary.contrastText, 0.7)
      : theme.palette.action.disabled,
    boxShadow: 'none'
  },
  textTransform : 'none'
}))

export const BglPrimaryOutlinedButtonBase = styled(Button)(({theme}) => ({
  color: theme.palette.primary.main,
  border: '1px solid',
  borderColor: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.main + '20',
  ":hover" : {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '40'
  },
  "&.Mui-disabled": {
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.5)
      : alpha(theme.palette.primary.main, 0.4),
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.3)
      : alpha(theme.palette.primary.main, 0.25),
    backgroundColor: 'transparent',
    border: '1px solid !important'
  },
  textTransform : 'none'
}))

export const BglSecondaryOutlinedButtonBase = styled(Button)(({theme}) => ({
  color: theme.palette.success.dark,
  border: '1px solid',
  borderColor: theme.palette.success.light,
  backgroundColor: theme.palette.success.light + '20',
  ":hover" : {
    borderColor: theme.palette.success.light,
    backgroundColor: theme.palette.success.light + '40'
  },
  "&.Mui-disabled": {
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.success.light, 0.3)
      : alpha(theme.palette.success.light, 0.6),
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.success.light, 0.5)
      : alpha(theme.palette.success.light, 0.4),
    backgroundColor: 'transparent',
    border: '1px solid !important'
  },
  textTransform : 'none'
}))

export const BglCloseOutlinedButtonBase = styled(Button)(({theme}) => ({
  color: theme.palette.error.main,
  border: '1px solid',
  borderColor: theme.palette.error.main,
  backgroundColor: theme.palette.error.main + '20',
  ":hover" : {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.main + '40'
  },
  "&.Mui-disabled": {
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.error.main, 0.5)
      : alpha(theme.palette.error.main, 0.4),
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.error.main, 0.3)
      : alpha(theme.palette.error.main, 0.25),
    backgroundColor: 'transparent',
    border: '1px solid !important'
  },
  textTransform : 'none'
}))

// ----------------------CLICK TRACKING --------------------------------------

// Pull a readable label out of button content (string, number, or nested nodes)
// so we can attach it to the analytics event.
const extractLabel = (node : ReactNode) : string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractLabel).join(' ').trim()
  if (isValidElement(node)) return extractLabel((node.props as any)?.children)
  return ''
}

// Wraps a styled base button so every click fires a generic "button clicked"
// event with the button's text. Pass `trackingLabel` to override the derived
// label (e.g. icon-only buttons) or `disableTracking` to opt out.
const withClickTracking = (Base : any, size : string, variant : string) => {
  return ({ onClick, trackingLabel, disableTracking, ...props } : any) => {

    const { rc } = useGlobalConfig()

    const handleClick = (event : MouseEvent<HTMLButtonElement>) => {
      if (!disableTracking) {
        const label = trackingLabel
          ?? props['aria-label']
          ?? props.title
          ?? extractLabel(props.children)

        rc?.analytics?.trackEvent(ANALYTICS_EVENTS.UI.BUTTON_CLICKED, {
          button_text    : label,
          button_variant : variant,
        })
      }
      onClick?.(event)
    }

    return (
      <Base
        size={size}
        variant={variant}
        {...props}
        onClick={handleClick}/>
    )
  }
}

// ----------------------LARGE BUTTONS --------------------------------------

export const BglPrimaryButton = withClickTracking(BglPrimaryButtonBase, 'large', 'contained')

export const BglSecondaryButton = withClickTracking(BglSecondaryButtonBase, 'large', 'contained')

export const BglCloseButton = withClickTracking(BglCloseButtonBase, 'large', 'contained')

// -----------------LARGE OUTLINED BUTTONS -------------------------------------

export const BglPrimaryOutlinedButton = withClickTracking(BglPrimaryOutlinedButtonBase, 'large', 'outlined')

// ----------------------SMALL BUTTONS --------------------------------------

export const BglPrimarySmallButton = withClickTracking(BglPrimaryButtonBase, 'small', 'contained')

export const BglSecondarySmallButton = withClickTracking(BglSecondaryButtonBase, 'small', 'contained')

export const BglCloseSmallButton = withClickTracking(BglCloseButtonBase, 'small', 'contained')

// -----------------SMALL OUTLINED BUTTONS -------------------------------------

export const BglPrimaryOutlinedSmallButton = withClickTracking(BglPrimaryOutlinedButtonBase, 'small', 'outlined')

export const BglSecondaryOutlinedSmallButton = withClickTracking(BglSecondaryOutlinedButtonBase, 'small', 'outlined')

export const BglCloseOutlinedSmallButton = withClickTracking(BglCloseOutlinedButtonBase, 'small', 'outlined')
